import { useMemo, useState, ChangeEvent } from "react";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { STATIC_REDIRECT_URLS } from "@iddataweb/idw-common-ui/constants";
import { StaticRedirectUrlTypes } from "@iddataweb/idw-common-ui/enums";
import { getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { LandingPageTypes } from "@enums/landing-page-types";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import styles from "./add-landing-page-modal.less";
import { BaseRedirectUrl } from "@idw-types/redirect-url-information";

type AddLandingPageModalProps = {
    selectedStaticLandingPageUrls: Array<string>;
    customRedirectUrlsMap: Record<string, BaseRedirectUrl>;
    customerRedirectUrlsMap: Record<string, BaseRedirectUrl>;
    landingPageType: LandingPageTypes;
    onModalClose: () => void;
    onClickContinue: (landingPageName: string) => void;
};

export const AddingLandingPageModal = (props: AddLandingPageModalProps) => {
    const {
        selectedStaticLandingPageUrls,
        landingPageType,
        customRedirectUrlsMap,
        customerRedirectUrlsMap,
        onModalClose,
        onClickContinue
    } = props;

    const availableStaticLandingPages = useMemo(() => {
        const selectedStaticLandingPageUrlsWithoutHostname = selectedStaticLandingPageUrls.map(url =>
            getRedirectUrlWithoutHostname(url)
        );
        return STATIC_REDIRECT_URLS
            .filter(redirectUrl => !selectedStaticLandingPageUrlsWithoutHostname.includes(redirectUrl));
    }, [selectedStaticLandingPageUrls, STATIC_REDIRECT_URLS]);

    const [state, setState] = useState({
        selectedStaticLandingPageType: availableStaticLandingPages.length > 0 ? availableStaticLandingPages[0] : null,
        landingPageName: ""
    });


    const staticLandingPageTypeOptions = useMemo(() => {
        return availableStaticLandingPages.map(redirectUrl => ({
            label: redirectUrl,
            value: redirectUrl
        }));
    }, [availableStaticLandingPages]);

    const selectedStaticLandingPageTypeOption = useMemo(() => {
        return staticLandingPageTypeOptions.find(lpto => lpto.value === state.selectedStaticLandingPageType);
    }, [state.selectedStaticLandingPageType]);

    const onChangeStaticLandingPageType = (newValue: SelectOption<StaticRedirectUrlTypes>) => {
        setState({
            ...state,
            selectedStaticLandingPageType: newValue.value
        });
    };

    const onChangeLandingPageName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            landingPageName: event.target.value
        });
    };

    const landingPageNameValidationError = useMemo(() => {
        if (landingPageType === LandingPageTypes.CUSTOM) {
            if (!state.landingPageName) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.landingPage.validation.empty}</span></ErrorAlert>;
            } else if (state.landingPageName.includes(" ")) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.landingPage.validation.noSpaces}</span></ErrorAlert>
            } else if(customRedirectUrlsMap[`[mockrp2-base]/display/${state.landingPageName}`]) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.duplicated}</span></ErrorAlert>
            }
        }

        return null;
    }, [state.landingPageName, landingPageType, customRedirectUrlsMap]);

    const redirectUrlValidationError = useMemo(() => {
        if (landingPageType === LandingPageTypes.CUSTOMER) {
            if (!state.landingPageName) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.empty}</span></ErrorAlert>;
            } else if (state.landingPageName.includes(" ")) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.noSpaces}</span></ErrorAlert>
            } else if(customerRedirectUrlsMap[state.landingPageName]) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.duplicated}</span></ErrorAlert>
            } else if (state.landingPageName.includes("/display")) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.customRedirectNotAllowed}</span></ErrorAlert>
            } 
        }

        return null;
    }, [state.landingPageName, landingPageType, customerRedirectUrlsMap]);

    const staticValidationError = useMemo(() => {
        if (landingPageType === LandingPageTypes.STATIC && state.selectedStaticLandingPageType === null) {
            return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.staticLandingPageType.validation.noneAvailable}</span></ErrorAlert>
        }

        return null;
    }, [state.selectedStaticLandingPageType, landingPageType]);

    const beforeInputElement = useMemo(() => {
        return <div className={styles.displayText}><span>display/</span></div>;
    }, []);

    const getFinalUrl = () => {
        if (landingPageType === LandingPageTypes.STATIC) {
            return `[mockrp2-base]/${state.selectedStaticLandingPageType}`;
        } else if (landingPageType === LandingPageTypes.CUSTOM) {
            return `[mockrp2-base]/display/${state.landingPageName}`;
        } else {
            return state.landingPageName;
        }
    };

    const onClickSave = () => {
        const finalUrl = getFinalUrl();
        onClickContinue(finalUrl);
        onModalClose();
    };

    return (
        <DefaultModal
            automationId="add-landing-page-modal"
            ariaLabelledBy="add-landing-page-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Modal.Header id={"add-landing-page-title"} closeButton>
                <Modal.Title>{resources.modals.addLandingPage.title(landingPageType)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {landingPageType === LandingPageTypes.STATIC && (
                    <>
                        <SelectFormGroup
                            automationId={`add-landing-page-static-select`}
                            controlId={`add-landing-page-static-select`}
                            ariaLabel={resources.modals.addLandingPage.inputs.staticLandingPageType.label}
                            label={resources.modals.addLandingPage.inputs.staticLandingPageType.label}
                            disabled={false}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            onChange={onChangeStaticLandingPageType}
                            options={staticLandingPageTypeOptions}
                            selectedOption={selectedStaticLandingPageTypeOption}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            layout={LayoutTypes.ROW}
                        />
                        {staticValidationError}
                    </>
                )}
                {landingPageType === LandingPageTypes.CUSTOM && (
                    <>
                        <InputFormGroup
                            ariaLabel={resources.modals.addLandingPage.inputs.landingPage.label}
                            automationId={`add-landing-page-name-input`}
                            controlId={`add-landing-page-name-input`}
                            disabled={false}
                            isRequired={true}
                            onChange={onChangeLandingPageName}
                            value={state.landingPageName}
                            beforeInputElement={beforeInputElement}
                            label={resources.modals.addLandingPage.inputs.landingPage.label}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            layout={LayoutTypes.ROW}
                        />
                        {landingPageNameValidationError}
                    </>
                )}
                {landingPageType === LandingPageTypes.CUSTOMER && (
                    <>
                        <InputFormGroup
                            ariaLabel={resources.modals.addLandingPage.inputs.redirectUrl.label}
                            automationId={`add-redirect-url-input`}
                            controlId={`add-redirect-url-input`}
                            disabled={false}
                            isRequired={true}
                            onChange={onChangeLandingPageName}
                            value={state.landingPageName}
                            label={resources.modals.addLandingPage.inputs.redirectUrl.label}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            layout={LayoutTypes.ROW}
                        />
                        {redirectUrlValidationError}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button id="add-landing-page-cancel-button" variant="secondary" onClick={onModalClose}>
                    {resources.common.buttons.cancel.label}
                </Button>
                <Button
                    id="add-landing-page-submit-button"
                    variant="primary"
                    onClick={onClickSave}
                    disabled={!!landingPageNameValidationError || !!staticValidationError || !!redirectUrlValidationError}
                >
                    {resources.common.buttons.save.label}
                </Button>
            </Modal.Footer>
        </DefaultModal>
    );
};